'use strict';
/**
 * Config-Dictionary — curated, human descriptions for configuration namespaces and individual keys
 * (the "what it means" overlay). Keyed by NamespaceKey, then by ConfigEntry Address (slash form) so
 * lookups are independent of how the object-editor renders the path. Missing entries simply mean "no
 * help text" — the dictionary is an optional overlay that can grow over time.
 *
 * The viewer translates a focused node's editor path -> Address (Config-Provenance.editorPathToAddress
 * after editorPathToLeafPath) to find the matching Keys entry.
 *
 * (v2 idea: promote this to its own editable config namespace so descriptions are authored in-app.)
 */

const CONFIG_DICTIONARY =
{
	MasterListCatalog:
	{
		Description: 'Per-entity reference/catalog list configuration. Each NamespaceValue is one entity (Material, PayItem, …); its object configures how that list is read and displayed.',
		Keys:
		{
			'/Filter': 'A FoxHound filter (FBV~…) applied when reading this list — restricts which records appear.',
			'/Columns': 'The columns shown for this list, in order.',
			'/HiddenColumns': 'Columns hidden by default in the list view.',
			'/Connections': 'Related-entity connections surfaced alongside this list.',
			'/DisplayName': 'The human label shown for this list in the UI.',
			'/Category': 'The grouping/category this list is filed under in navigation.',
		},
	},
	ModuleAccess:
	{
		Description: 'Which optional product modules are enabled. Each NamespaceValue is a module (e.g. Contracts); `enabled` turns it on for the scope.',
		Keys:
		{
			'/enabled': 'Whether this module is enabled at the current scope.',
		},
	},
	Permissions:
	{
		Description: 'Role/permission toggles applied across the cascade.',
		Keys:
		{
			'/enabled': 'Whether this permission is granted at the current scope.',
		},
	},
	ApprovalsService:
	{
		Description: 'Approval-workflow configuration: routing, thresholds, and notifications.',
		Keys: {},
	},
	ApplicationSettings:
	{
		Description: 'General application behaviour and feature toggles.',
		Keys: {},
	},
	FinanceService:
	{
		Description: 'Finance service configuration. Each NamespaceValue is one area of the service — PaymentFulfillment, Forms, Digest, Accounting, … — and its object holds that area\'s settings.',
		Keys:
		{
			'/enableFormDataIngest': 'Whether saving a payment form makes the finance service read its FormData and write the accounting rows. A bridge for forms that have not yet adopted the payment entries endpoint; off means a form logs its own entries instead.',
			'/useMockFinancialReader': 'Forms: whether the default payment form falls back to its mock financial reader, which re-reads every earlier payment\'s FormData in the browser, instead of reading the accounting model. Off by default; an opt-out kept while the accounting flow soaks.',
		},
	},
};

/**
 * Namespace-level description, or '' when unknown.
 * @param {string} pNamespaceKey
 * @return {string}
 */
function describeNamespace(pNamespaceKey)
{
	const tmpEntry = CONFIG_DICTIONARY[pNamespaceKey];
	return (tmpEntry && tmpEntry.Description) || '';
}

/**
 * Per-key (Address) description, or '' when unknown.
 * @param {string} pNamespaceKey
 * @param {string} pAddress slash-form Address (e.g. '/Filter')
 * @return {string}
 */
function describeKey(pNamespaceKey, pAddress)
{
	const tmpEntry = CONFIG_DICTIONARY[pNamespaceKey];
	return (tmpEntry && tmpEntry.Keys && tmpEntry.Keys[pAddress]) || '';
}

module.exports = {
	CONFIG_DICTIONARY,
	describeNamespace,
	describeKey,
};
